import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

import { addMonths, startOfDay } from "date-fns";

export interface StartFromPresetOption {
  label: string;
  months: number | null;
}

// Compact labels for the dialog's criteria rail (narrow column).
export const DIALOG_START_FROM_PRESETS: StartFromPresetOption[] = [
  { label: "Soonest", months: null },
  { label: "1 mo", months: 1 },
  { label: "3 mo", months: 3 },
  { label: "6 mo", months: 6 },
  { label: "1 yr", months: 12 }
];

// Full-word labels for the checkout "Schedule next appointment?" modal.
export const CHECKOUT_START_FROM_PRESETS: StartFromPresetOption[] = [
  { label: "Soonest", months: null },
  { label: "3 months", months: 3 },
  { label: "6 months", months: 6 },
  { label: "1 year", months: 12 }
];

export type StartFromChoice = number | null | "custom";

// 12 → "1 year" instead of "12 months": intervals are configured in months
// but staff talk in years past the one-year mark.
export function formatIntervalMonths(months: number): string {
  if (months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? "1 year" : `${years} years`;
  }
  return months === 1 ? "1 month" : `${months} months`;
}

/**
 * Presets mean "on or after this date": a 6-month recall is booked no sooner
 * than 6 months out, so the search window simply starts at the target date.
 */
export function startFromDateForMonths(months: number | null): Date | null {
  return months == null ? null : addMonths(startOfDay(new Date()), months);
}

interface StartFromPresetsProps {
  readonly value: StartFromChoice;
  readonly onChange: (value: StartFromChoice) => void;
  readonly options?: StartFromPresetOption[];
  readonly allowCustom?: boolean;
}

export default function StartFromPresets({
  value,
  onChange,
  options = DIALOG_START_FROM_PRESETS,
  allowCustom = false
}: StartFromPresetsProps) {
  // A configured interval outside the preset list (e.g. 9 or 18 months) must
  // still be visible as the selected chip — otherwise it silently drives the
  // search with nothing on screen saying so.
  const offPresetValue =
    typeof value === "number" &&
    !options.some((preset) => preset.months === value)
      ? value
      : null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
      {options.map((preset) => {
        const selected = value === preset.months;
        return (
          <Chip
            key={preset.label}
            label={preset.label}
            size="small"
            clickable
            color={selected ? "primary" : "default"}
            variant={selected ? "filled" : "outlined"}
            onClick={() => onChange(preset.months)}
            data-testid={`start-from-preset-${preset.months ?? "soonest"}`}
          />
        );
      })}
      {offPresetValue != null && (
        <Chip
          label={formatIntervalMonths(offPresetValue)}
          size="small"
          clickable
          color="primary"
          variant="filled"
          onClick={() => onChange(offPresetValue)}
          data-testid={`start-from-preset-${offPresetValue}`}
        />
      )}
      {allowCustom && (
        <Chip
          label="Custom…"
          size="small"
          clickable
          color={value === "custom" ? "primary" : "default"}
          variant={value === "custom" ? "filled" : "outlined"}
          onClick={() => onChange("custom")}
          data-testid="start-from-preset-custom"
        />
      )}
    </Stack>
  );
}
